/*
 *   File : login.js
 *   Author : https://evoqins.com
 *   Description :Login page of raayi
 *   Integrations : js-cookie
 *   Version : 1.0.0
*/

// custom packages
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from "js-cookie";
import { toast } from 'react-toastify';

// custom components
import { CustomTextInput, OTPInput } from '../../Components/FormElements';
import { PrimaryButton } from "../../Components/Buttons";
import { Icon } from '../../Components/Icon';
import { APIService } from '../../Service';

// custom style 
import style from "../../Style/Component/auth.module.scss";
import Store from '../../Store/store';

const Login = () => {

    const inputRef = useRef(null);
    const navigate = useNavigate();

    const [userName, setUserName] = useState("");
    const [userNameError, setUserNameError] = useState("");

    const [password, setPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");

    const [loginFlow, setLoginFlow] = useState(1);

    const [otpValue, setOtpValue] = useState("");

    const [otpValueError, setOtpValueError] = useState("");

    const [focusedInput, setFocusedInput] = useState(2);
    const [seconds, setSeconds] = useState(30);
    const [timerActive, setTimerActive] = useState(false);
    const [loader, setLoader] = useState(false);


    useEffect(() => {
        setUserNameError("");
    }, [userName]);

    useEffect(() => {
        setPasswordError("");
    }, [password]);

    useEffect(() => {
        setOtpValueError("");
    }, [otpValue]);

    useEffect(() => {
        let timer;
        if (timerActive && seconds > 0) {
            timer = setInterval(() => {
                setSeconds((prev) => prev - 1);
            }, 1000);
        } else if (seconds === 0) {
            setTimerActive(false);
            setSeconds(30);
        }

        return () => clearInterval(timer);
    }, [timerActive, seconds]);

    // handle login  
    const _handleLogin = () => {
        let is_valid = true;

        if (userName.trim().length === 0) {
            setUserNameError("Enter your user name");
            is_valid = false;
        };

        if (password.trim().length === 0) {
            setPasswordError("Password cannot be empty");
            is_valid = false;
        };

        if (is_valid === true) {
            _login();
            setLoader(true);
        };
    };

    // handle navigate forgot password 
    const _handleForgotPassword = () => {
        navigate("/forgot-password");
    };

    // handle back
    const _handleBack = () => {
        setLoginFlow(1);
        setOtpValue("");
        setOtpValueError("");
        setFocusedInput(2);
        setTimerActive(false);
        setSeconds(30);
    };

    //handle Otp change
    const _handleChangeOTP = (value) => {
        setOtpValue(value);
    };

    // handle resend OTP 
    const _handleResendOtp = () => {
        _login();
    };

    // handle verify otp
    const _handleVerify = () => {
        let is_valid = true;
        if (otpValue.length < 4) {
            setOtpValueError("Please enter received OTP");
            is_valid = false;
        }

        if (is_valid === true) {
            _handleVerifyOtp();
            setLoader(true);
        }
    };


    // API - Get login otp
    const _login = () => {

        let is_post = true;
        let url = 'auth/login';
        let data = {
            "username": userName,
            "password": password
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setLoginFlow(2);

                setOtpValue("");
                setFocusedInput(2);
                setTimerActive(true);
                setSeconds(30);
            }
            else {
                toast.dismiss();
                toast.success(response.message, { type: "error" });
            }
            setLoader(false);
        });
    };

    // API - Verify OTP
    const _handleVerifyOtp = async () => {
        let url = 'auth/verify-otp';

        let data = {
            username: userName,
            otp: otpValue,
            type: "LOGIN"
        };

        APIService(true, url, data).then((response) => {

            if (response.status_code === 200) {
                const token = response.access_token.token;

                let permitted_modules = response.access_token.modules;
                localStorage.setItem('modules', JSON.stringify(permitted_modules));
                Cookies.set('access_token', token, {
                    domain: process.env.REACT_APP_ENV_DOMAIN,
                    path: '/',
                    expires: 1
                });

                window.location.replace("/");
            } else {
                toast.dismiss();
                toast.error(response.message, { type: "error" });
            }
            setLoader(false);
        });
    };


    return (
        <section className={`${style.e_auth_wrapper} padding-64px-tb`}>
            <div className='container px-lg-0 px-md-0 px-sm-0 px-3'>
                <div className='text-center'>
                    <img src={require("../../Assets/Image/logo.svg").default}
                        draggable={false}
                        className='user-select-none'
                        alt='rayi-logo' />
                </div>
                <div className='row padding-32px-top'>
                    <div className='offset-lg-4 offset-md-2 offset-sm-2 offset-1 col-lg-4 col-md-8 col-sm-8 col-10 px-lg-0'>
                        <div className={`${style.e_form_wrapper} e-bg-white border-radius-16 padding-40px-all`}>
                            {loginFlow === 1 ?
                                // login section
                                <>
                                    <h3 className='mb-4 e-text-chinese-black e-primary-font e-font-24 e-font-weight-700 line-height-28px'>
                                        Admin Login
                                    </h3>

                                    {/* Username */}
                                    <CustomTextInput label="Username"
                                        placeholder="Enter username"
                                        type="text"
                                        autoFocus={true}
                                        required={true}
                                        value={userName}
                                        error={userNameError}
                                        className="w-100"
                                        onKeyPress={_handleLogin}
                                        handleChange={(e) => setUserName(e)} />

                                    {/* Password */}
                                    <CustomTextInput label="Password"
                                        placeholder="Enter password"
                                        type="password"
                                        required={true}
                                        showIcon={true}
                                        value={password}
                                        error={passwordError}
                                        wrapperClass="pt-3 pb-3"
                                        className="w-100 e-letter-spacing-2px"
                                        onKeyPress={_handleLogin}
                                        handleChange={(e) => setPassword(e)} />

                                    <div className='mb-3 pb-1 d-flex justify-content-end'>
                                        <span className='e-text-blue-violet e-primary-font e-font-14 e-font-weight-400 line-height-18px text-decoration-hover-underline cursor-pointer'
                                            onClick={_handleForgotPassword}>
                                            Forgot Password?
                                        </span>
                                    </div>

                                    {/* Login button */}
                                    <PrimaryButton name="Login"
                                        className="w-100"
                                        loader={loader === true ? true : false}
                                        onClick={_handleLogin} />
                                </>
                                :
                                // Otp verify section 
                                <>
                                    <Icon icon='back'
                                        size="1.25rem"
                                        className="cursor-pointer"
                                        onClick={_handleBack} />
                                    <h3 className='my-4 e-text-chinese-black e-primary-font e-font-24 e-font-weight-700 line-height-28px'>
                                        OTP Verification
                                    </h3>

                                    {/* OTP input */}
                                    <OTPInput label="Enter otp sent to the mail id"
                                        required={true}
                                        ref={inputRef}
                                        value={otpValue}
                                        spellCheck="false"
                                        error={otpValueError}
                                        focusedInput={focusedInput}
                                        onFocus={() => setFocusedInput(2)}
                                        onBlur={() => setFocusedInput(0)}
                                        onKeyPress={_handleVerify}
                                        onChange={_handleChangeOTP} />
                                    <p className='pt-3 mt-1 mb-0 e-text-taupe-gray  e-primary-font e-font-14 e-font-weight-400 line-height-18px'>
                                        Didn’t get code yet?
                                        <span className={`${timerActive ? 'pe-none' : ''} ps-1 e-text-blue-violet text-decoration-hover-underline cursor-pointer`}
                                            onClick={_handleResendOtp}>
                                            {timerActive ? `Resend OTP in ${seconds}s` : 'Resend OTP'}
                                        </span>
                                    </p>

                                    {/* Login button */}
                                    <PrimaryButton name="Verify & Continue"
                                        className="w-100 mt-4"
                                        loader={loader === true ? true : false}
                                        onClick={_handleVerify} />
                                </>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Login;
